import { atom } from "jotai";
import type { UserProfile } from "../services/authService";

/**
 * Cookie-aware auth state for the application SPA.
 *
 * Pre-migration the codebase synchronously read `localStorage.@Eriol/system/accessKey`
 * everywhere to answer "is the user logged in?". That works in Electron (where
 * the deep-link still hands a token across processes via localStorage), but in
 * browser context the HttpOnly cookie can't be inspected from JS — we have to
 * call /auth/me to find out, and that's inherently async.
 *
 * These atoms are the bridge: `useAuthBootstrap` mounts once at app start,
 * calls /auth/me, and transitions `authBootstrapStateAtom` from "checking"
 * to "authenticated" (populating `currentUserAtom`) or "unauthenticated".
 *
 * Consumers that need synchronous gating (RouteGuard, withAuth) must:
 *   - Render a spinner while state === "checking" (avoid a redirect flash).
 *   - Treat "unauthenticated" as the trigger to send the user to /login.
 *   - NEVER fall back to reading localStorage directly — that defeats the
 *     migration (the whole point is that browser sessions don't write it).
 */

export type AuthBootstrapState =
  | "checking"
  | "authenticated"
  | "unauthenticated";

export const authBootstrapStateAtom = atom<AuthBootstrapState>("checking");

export const currentUserAtom = atom<UserProfile | null>(null);

/**
 * Derived flag — handy for `if (!isAuthenticated) navigate(...)` style code.
 * Note: returns `false` while bootstrapping, so callers MUST also check
 * `authBootstrapStateAtom === "checking"` to avoid a premature redirect.
 */
export const isAuthenticatedAtom = atom(
  (get) => get(authBootstrapStateAtom) === "authenticated",
);
